import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpInterceptor,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of, from as rxFrom  } from 'rxjs';
import { catchError, switchMap, filter, take, finalize, tap, map, mergeMap, timeout } from 'rxjs/operators';
import { AuthService } from './auth-service.service';
import { LoggerService } from '../logger-service/logger.service';
import { environment } from '../../../../environments/environment';
import { Art57Section } from '../../ui/Art57Section/Art57Section';
import { ConfirmationService } from 'primeng/api';
import { MaintenanceService } from '../maintenance-service/maintenance.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false; // Flag to indicate if a token refresh is in progress
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private loggerService: LoggerService,
    private confirmationService: ConfirmationService,
    public injector: Injector,
    private maintenanceService: MaintenanceService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).
    pipe(
      catchError((err) => this.handleError(err, req, next))
    );
  }

  private handleError(
    err: HttpErrorResponse,
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {



  const isBlobJson = err?.error instanceof Blob && err.error.type?.includes('application/json');
  if (isBlobJson) {
    return rxFrom(err.error.text()).pipe(
      mergeMap((text) => {
        let parsed: any;
        try { parsed = JSON.parse(text as string); } catch { parsed = { message: text }; }

        // Construye un nuevo HttpErrorResponse con el JSON ya en err.error
        const transformed = new HttpErrorResponse({
          error: parsed,
          headers: err.headers,
          status: err.status,
          statusText: err.statusText,
          url: err.url ?? undefined, // importante: undefined, no null
        });

        // Re-entrar a la misma lógica con el error normalizado
        return this.handleError(transformed, req, next);
      })
    );
  }
    //403, 401 - session token expired
    //403 + errorcode, 401 + errorcode - user does not have permissions to perform the action in the backend

    if (err.status === 403 && !err.error?.errorcode) {
      this.loggerService.debug('Session token expired (403). Requesting to handle this error.');
      return this.handleTokenError(req, next, 'sessionToken');
    } else if (err.status === 401 && !err.error?.errorcode) {
      this.loggerService.debug('Access token expired (401). Requesting to handle this error.');
      return this.handleTokenError(req, next, 'accessToken');
    } else if (err.error?.errorcode) { // errors with "backend" error codes - SERVER_COMMUNICATION, BAD_USER_INFO, SUSPICIOUS_REQUEST, REQUEST_TIMEOUT
      if (!this.handleErrorWithErrorCodes(err)) {
        return of ();
      }
    } else {
      // certain errors handled in other layers and/or by global handler
    }

    this.injector.get(Art57Section).removeIsLoading();
    this.loggerService.err('Handled error in interceptor, but not related to access token or session token expiration. Bubbling up the error. (AuthInterceptor.handleError)', err);
    
    return throwError(() => err );
  }

  private handleTokenError(
    req: HttpRequest<any>,
    next: HttpHandler,
    tokenType: 'sessionToken' | 'accessToken'
  ): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null); // Reset the subject

      // Determine the appropriate refresh operation
      const refreshOperation =
        tokenType === 'sessionToken'
          ? this.refreshSessionToken()
          : this.refreshAccessTokenAndSessionToken();

      return refreshOperation.pipe(
        switchMap(() => {
          return next.handle(this.getModifiedRequest(req, false));
        }),
        catchError((error) => {
          this.loggerService.err('Error during token refresh (AuthInterceptor.handleTokenError):', error);
          return throwError(() => error);
        }),
        finalize(() => {
          this.isRefreshing = false; // Reset the flag after completion
        })
      );
    } else {
      // Queue the request until the tokens are refreshed
      const logDebugText = tokenType === 'accessToken'
        ? 'Session token refresh in progress after Access token refresh. Queuing request.'
        : 'Session token refresh in progress. Queuing request.';
      this.loggerService.debug(`${logDebugText}`);
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap(() => next.handle(this.getModifiedRequest(req, true)))
      );
    }
  }

  // Helper function to refresh the access token and session token
  private refreshAccessTokenAndSessionToken(): Observable<void> {
    this.loggerService.debug('Requesting new Access Token.');
    return this.authService.acquireAndStoreAccessTokenObservable().pipe(
      switchMap(() => {
        this.loggerService.debug('Access token successfully refreshed. Requesting Session token after the Access token. (AuthInterceptor.refreshAccessTokenAndSessionToken)');
        return this.refreshSessionToken();
      }),
      map(() => void 0)
    );
  }

  // Helper function to refresh the session token
  private refreshSessionToken(): Observable<void> {
    const organisation = this.authService.getOrganisation();
    // Check if organisation is empty
    if (!organisation) {
      this.loggerService.debug('Organisation is empty. Skipping session token refresh. This might happen if you are in the 1st step of login and the user session is not created yet. This scenario is handled.');
      this.loggerService.debug('Notifying all waiting requests to be modified with the new Access token');
      // Notify waiting requests even if session token is not refreshed, scenario when you are in 1.step of login and MFA expires and you refresh the page or click on button Select
      this.refreshTokenSubject.next(this.authService.getAccessToken());
      return of(void 0);
    }

    this.loggerService.debug('Requesting new Session token.');
    return this.authService.getUserData(organisation).pipe(
      tap((data) => {
        this.authService.updateSessionWithPrincipal(data.user);
        this.loggerService.debug('Session token successfully refreshed. (AuthInterceptor.refreshSessionToken.)');
        this.loggerService.debug('Notifying all waiting requests to be modified with the new token(s). (AuthInterceptor.refreshSessionToken.)');
        this.refreshTokenSubject.next(data.user.accessToken); // Notify all waiting requests
      }),
      map(() => void 0)
    );
  }

  // Modify the request with the new access token and session token
  private getModifiedRequest(req: HttpRequest<any>, isQueuedRequest: boolean = false): HttpRequest<any> {
    const requestType = isQueuedRequest ? 'Queued request' : 'Original request';

    let headers = req.headers.set('Authorization', `Bearer ${this.authService.getAccessToken()}`);
    const sessionToken = this.authService.getSessionToken();

    const tokenInfo = sessionToken
      ? 'Adding both Access Token and Session Token to the headers.'
      : 'Adding only Access Token to the headers.';

    this.loggerService.debug(`Modifying headers for waiting request (${requestType}). ${tokenInfo}`);

    if (sessionToken) {
      headers = headers.set('AuthSession', `${sessionToken}`);
    }

    return req.clone({ headers });
  }

  private handleErrorWithErrorCodes(err: any):boolean {
    const art57Section = this.injector.get(Art57Section);
    const errorCode = err?.error?.errorcode;
    const errorPath = err?.error?.path;
    let throwError = true;
    if (errorCode) {
      if (errorCode === "SERVER_COMMUNICATION") {
        // SERVER_COMMUNICATION
        this.showServerCommunicationError(err);
      } else if (errorCode.startsWith("MAINTENANCE_")){
        this.maintenanceService.enableMaintenance(errorCode);
      } else if (errorCode.startsWith("REQUEST_TIMEOUT")){
        throwError = false;
        art57Section.showWarningTooltipMessage(err.error?.message);
      } else {
        // SUSPICIOUS_REQUEST or BAD_USER_INFO
        // but excluding errors for the lookups in the WebTrader section or Create and Send Product section (they are displayed in other way)
        if (!(
          (art57Section.isWebtraderSection() || art57Section.isCreateAndSendProductSection())
          && errorPath?.includes("/api/lookups/")
        )) {
          art57Section.showErrorTooltipMessage(err.error?.message);
        }
      }
    }
    return throwError;
  }

  private showServerCommunicationError(err: any) {
    this.confirmationService.confirm({
      header: 'An error happened',
      message: 'The operation failed. ' + err?.error?.message + '.<br><br>' + 'Please try again and if the issue still persists contact us through <a href=' + environment.serviceDeskURL + ' target="_blank">EMA Service Desk<a/>',
      icon: 'pi pi-times-circle',
      acceptLabel: 'Close',
      rejectVisible: false,
      accept: () => {
        // do nothing
      }
    });
  }
}

function from(arg0: Promise<string>) {
  throw new Error('Function not implemented.');
}

